import { useState } from "react";
import { toast } from "sonner";
import { Star, MessageSquareHeart, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function Stars({ value, onChange, size = 22 }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Valutazione" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} stelle`} data-testid={`feedback-star-${n}`}
          onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onFocus={() => setHover(n)} onBlur={() => setHover(0)}
          className="cursor-pointer rounded-full p-0.5 transition-transform hover:scale-125 focus-visible:scale-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black">
          <Star size={size} className={`transition-colors ${n <= shown ? "fill-[#E0B94B] text-[#E0B94B]" : "text-neutral-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function FeedbackSection() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "", rating: 5 });
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { ...form, name: form.name || undefined, email: form.email || undefined });
      toast.success(t("feedback_thanks"));
      setForm({ name: "", email: "", message: "", rating: 5 }); setOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <section id="feedback" data-testid="feedback-section" className="mx-auto max-w-md px-6 pb-12">
      <button type="button" data-testid="feedback-toggle" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 rounded-2xl border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-600 hover:border-black hover:text-black transition-colors">
        <span className="flex items-center gap-2"><MessageSquareHeart size={16} />{t("feedback_open")}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={send} data-testid="feedback-form" className="mt-3 rounded-3xl border border-neutral-200 p-5 space-y-3 bg-white">
          <p className="text-xs text-neutral-500">{t("feedback_desc")}</p>
          <div>
            <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
            <p className="text-[11px] text-neutral-400 mt-1">{t("feedback_rating_hint")} · {form.rating}/5</p>
          </div>
          <Textarea data-testid="feedback-message" required minLength={3} maxLength={1000} rows={3} placeholder={t("feedback_placeholder")}
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="feedback-name" placeholder={t("feedback_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" />
            <Input data-testid="feedback-email" type="email" placeholder={t("feedback_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" />
          </div>
          <Button data-testid="feedback-submit" type="submit" disabled={busy} variant="outline" className="w-full h-11 rounded-full">{busy ? "…" : t("feedback_send")}</Button>
        </form>
      )}
    </section>
  );
}
